import React, { useEffect, useRef, useState } from 'react';

import {
  CameraOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  UserOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Image,
  Modal,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import {
  ActiveShift,
  AdminCityRow,
  AdminUniversity,
  AdminUserDetail,
  LeadTag,
  ProfileStatusEntry,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { DocumentValidator } from 'utils/DocumentValidator';
import { applySavedLeadTags } from 'utils/LeadTagGridHelper';

import { useThemeLayoutConfig } from '../../../config/ThemeDashboardLayoutConfig';
import appI18n from '../../../config/i18n';
import LeadTagsEditor from './LeadTagsEditor';
import StatusIndicator from './StatusIndicator';
import BasicInfoSection from './sections/BasicInfoSection';
import EvaluationSection from './sections/EvaluationSection';
import IdentitySection from './sections/IdentitySection';
import OtherSection from './sections/OtherSection';
import PaymentSections from './sections/PaymentSections';
import { useConfirmDiscard } from './useConfirmDiscard';
import {
  useLeadPaymentCatalogs,
  useLeadProfileCatalogs,
} from './useLeadCatalogs';

const { Title } = Typography;

export interface DetailLeadNavigation {
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
  loading?: boolean;
  /** Posicion del lead dentro del listado filtrado (1-based). null cuando el
   * lead ya no forma parte del filtro actual (ver `outOfFilter`). */
  position?: { current: number; total: number } | null;
  /** true cuando el lead dejo de coincidir con el filtro actual (p. ej. tras
   * cambiar su estado desde el propio modal). */
  outOfFilter?: boolean;
}

interface DetailLeadModalProps {
  isOpen: boolean;
  leadId: string;
  handleModalCancel: () => void;
  onAfterUpdate: () => void;
  statuses: ProfileStatusEntry[];
  /**
   * Cuando se provee, el modal muestra flechas laterales para navegar entre
   * leads (solo se usa desde la escena /portal-go/leads). Si se omite, el modal
   * se comporta como antes (sin navegacion) en el resto de call-sites.
   */
  navigation?: DetailLeadNavigation;
}

const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

const getDocumentTypeKey = (countryCode: string): string => {
  const upper = (countryCode || '').toUpperCase();
  if (upper === 'CL' || upper === 'PE' || upper === 'CO') return upper;
  return 'default';
};

const getCityTypeKey = (countryCode: string): string => {
  const upper = (countryCode || '').toUpperCase();
  if (upper === 'CL' || upper === 'PE' || upper === 'CO') return upper;
  return 'default';
};

function DetailLeadModal({
  isOpen,
  leadId,
  handleModalCancel,
  onAfterUpdate,
  statuses,
  navigation,
}: DetailLeadModalProps) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { theme } = useThemeLayoutConfig();
  const dashboardConfig = useSelector((state: any) => state.dashboardConfig);
  const primaryColor =
    dashboardConfig?.primaryColor ||
    (theme.token?.colorPrimary as string | undefined) ||
    '#1677ff';

  const [loading, setLoading] = useState<boolean>(true);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);

  const [cities, setCities] = useState<AdminCityRow[]>([]);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);

  const [universities, setUniversities] = useState<AdminUniversity[]>([]);
  const [universitiesLoading, setUniversitiesLoading] =
    useState<boolean>(false);

  const [shifts, setShifts] = useState<ActiveShift[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState<boolean>(false);

  // Kept on the modal (not the section) so the per-country cache survives
  // navigating between leads.
  const profileCatalogs = useLeadProfileCatalogs(
    detail?.user.countryCode || '',
    isOpen,
    (catalog, error) =>
      message.error(
        (error.message as string) ||
          t(`scenes.leads.scene.errors.fetch_${catalog}`),
        3.5
      )
  );

  // Only leads with payment data (countries with payment catalogs) load them.
  const paymentCatalogs = useLeadPaymentCatalogs(
    detail?.payment ? detail.user.countryCode : '',
    isOpen,
    (catalog, error) =>
      message.error(
        (error.message as string) ||
          t(`scenes.leads.scene.errors.fetch_${catalog}`),
        3.5
      )
  );

  const [avatarUploading, setAvatarUploading] = useState<boolean>(false);
  const [avatarPreviewVisible, setAvatarPreviewVisible] =
    useState<boolean>(false);

  // Unsaved tag edits (reported by LeadTagsEditor) guard leaving the lead.
  const { setDirty: setTagsDirty, confirmDiscard: confirmDiscardTags } =
    useConfirmDiscard();

  // The lead currently on screen: a tags save can resolve after navigating.
  const leadIdRef = useRef<string>(leadId);
  leadIdRef.current = leadId;

  useEffect(() => {
    if (isOpen) {
      fetchAll();
    } else {
      setDetail(null);
      setCities([]);
      setUniversities([]);
      setShifts([]);
    }
  }, [isOpen, leadId]);

  // Al cambiar de lead (navegacion con flechas) volvemos el scroll del modal al
  // inicio, igual que si se abriera un modal nuevo.
  useEffect(() => {
    if (!isOpen) return;
    const body = document.querySelector(
      '.lead-detail-modal .ant-modal-body'
    ) as HTMLElement | null;
    if (body) body.scrollTop = 0;
  }, [leadId, isOpen]);

  // Navegacion con teclado (solo cuando hay navegacion habilitada y el foco no
  // esta en un campo editable, para no interferir con la edicion de formularios).
  useEffect(() => {
    if (!navigation || !isOpen) return;
    const handler = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const tag = (active?.tagName || '').toLowerCase();
      const isEditable =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        !!active?.isContentEditable;
      if (isEditable) return;
      if (e.key === 'ArrowLeft' && navigation.canPrev && !navigation.loading) {
        confirmDiscardTags(navigation.onPrev);
      } else if (
        e.key === 'ArrowRight' &&
        navigation.canNext &&
        !navigation.loading
      ) {
        confirmDiscardTags(navigation.onNext);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigation, isOpen]);

  const fetchAll = async () => {
    setLoading(true);
    const response = await LeadService.getById(leadId);

    if (!response.success) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.leads.components.detail_lead_modal.errors.fetch_lead_failed'
          ),
        3.5
      );
      handleModalCancel();
      setLoading(false);
      return;
    }

    const data = response.data as AdminUserDetail;
    setDetail(data);
    setLoading(false);

    fetchCities(data.user.countryCode);
    fetchUniversities(data.user.countryCode);
    fetchShifts();
  };

  const fetchCities = async (countryCode: string) => {
    setCitiesLoading(true);
    const response = await LeadService.getCities(countryCode);
    if (response.success) {
      setCities(response.data as AdminCityRow[]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.leads.scene.errors.fetch_cities'),
        3.5
      );
    }
    setCitiesLoading(false);
  };

  const fetchUniversities = async (countryCode: string) => {
    setUniversitiesLoading(true);
    const response = await LeadService.getUniversities(countryCode);
    if (response.success) {
      setUniversities(response.data as AdminUniversity[]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.leads.scene.errors.fetch_universities'),
        3.5
      );
    }
    setUniversitiesLoading(false);
  };

  const fetchShifts = async () => {
    setShiftsLoading(true);
    const response = await LeadService.getShifts();
    if (response.success) {
      setShifts(response.data as ActiveShift[]);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.leads.scene.errors.fetch_shifts'),
        3.5
      );
    }
    setShiftsLoading(false);
  };

  const handleTagsSaved = (tags: LeadTag[], savedLeadId: string) => {
    setDetail((prev) =>
      applySavedLeadTags(prev, savedLeadId, leadIdRef.current, tags)
    );
    onAfterUpdate();
  };

  const handleSectionUpdated = (next: AdminUserDetail) => {
    setDetail(next);
    onAfterUpdate();
  };

  const uploadProps: UploadProps = {
    accept: 'image/png,image/jpeg,image/jpg,image/webp',
    showUploadList: false,
    beforeUpload: (file) => {
      if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
        message.error(
          t('scenes.leads.components.detail_lead_modal.avatar.format_error')
        );
        return Upload.LIST_IGNORE;
      }
      if (file.size > AVATAR_MAX_BYTES) {
        message.error(
          t('scenes.leads.components.detail_lead_modal.avatar.size_error')
        );
        return Upload.LIST_IGNORE;
      }

      (async () => {
        setAvatarUploading(true);
        message.open({
          type: 'info',
          content: t(
            'scenes.leads.components.detail_lead_modal.avatar.uploading'
          ),
          key: 'lead-avatar',
          duration: 0,
        });
        const response = await LeadService.updateAvatar(leadId, file);
        message.destroy('lead-avatar');
        if (response.success) {
          const data = response.data as { avatarUrl: string | null };
          if (detail) {
            setDetail({
              ...detail,
              profile: { ...detail.profile, avatarUrl: data.avatarUrl },
            });
          }
          message.success(
            t('scenes.leads.components.detail_lead_modal.avatar.upload_success')
          );
          onAfterUpdate();
        } else {
          const error = response.data as ApiErrorData;
          message.error(
            (error.message as string) ||
              t(
                'scenes.leads.components.detail_lead_modal.avatar.upload_failed'
              ),
            3.5
          );
        }
        setAvatarUploading(false);
      })();

      return Upload.LIST_IGNORE;
    },
  };

  const handleClearAvatar = async () => {
    setAvatarUploading(true);
    message.open({
      type: 'info',
      content: t('scenes.leads.components.detail_lead_modal.avatar.deleting'),
      key: 'lead-avatar',
      duration: 0,
    });
    const response = await LeadService.clearAvatar(leadId);
    message.destroy('lead-avatar');
    if (response.success && detail) {
      setDetail({
        ...detail,
        profile: { ...detail.profile, avatarUrl: null },
      });
      message.success(
        t('scenes.leads.components.detail_lead_modal.avatar.delete_success')
      );
      onAfterUpdate();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.leads.components.detail_lead_modal.avatar.delete_failed'),
        3.5
      );
    }
    setAvatarUploading(false);
  };

  const renderHeader = () => {
    if (!detail) return null;
    const status = statuses.find((s) => s.code === detail.profile.statusCode);

    const countryCode = detail.user.countryCode;
    const notAvailable = t(
      'scenes.leads.components.detail_lead_modal.header.not_available'
    );

    const documentTypeLabel = t(
      `scenes.leads.components.detail_lead_modal.header.document_types.${getDocumentTypeKey(
        countryCode
      )}`
    );
    const rawDocument = detail.profile.rut;
    const documentValue = rawDocument
      ? (countryCode || '').toUpperCase() === 'CL'
        ? DocumentValidator.formatByCountry(rawDocument, countryCode)
        : rawDocument
      : notAvailable;

    const cityTypeLabel = t(
      `scenes.leads.components.detail_lead_modal.header.city_types.${getCityTypeKey(
        countryCode
      )}`
    );
    const cityValue = detail.city?.cityName || notAvailable;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '4px 0 16px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          marginBottom: 16,
        }}
      >
        <Spin
          spinning={avatarUploading}
          indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
        >
          <div className="lead-avatar-wrapper">
            {detail.profile.avatarUrl ? (
              <Avatar size={56} src={detail.profile.avatarUrl} />
            ) : (
              <Avatar size={56} icon={<UserOutlined />} />
            )}
            {detail.profile.avatarUrl && !avatarUploading && (
              <div className="lead-avatar-overlay">
                <Tooltip
                  title={t(
                    'scenes.leads.components.detail_lead_modal.avatar.preview'
                  )}
                >
                  <Button
                    icon={<ZoomInOutlined />}
                    onClick={() => setAvatarPreviewVisible(true)}
                  />
                </Tooltip>
                <Tooltip
                  title={t(
                    'scenes.leads.components.detail_lead_modal.avatar.delete'
                  )}
                >
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={handleClearAvatar}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </Spin>
        {detail.profile.avatarUrl && (
          <Image
            src={detail.profile.avatarUrl}
            style={{ display: 'none' }}
            preview={{
              visible: avatarPreviewVisible,
              onVisibleChange: (v) => setAvatarPreviewVisible(v),
              toolbarRender: (
                _,
                {
                  transform: { scale },
                  actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn },
                }
              ) => (
                <Space
                  size={16}
                  style={{
                    padding: '8px 18px',
                    background: 'rgba(0,0,0,0.45)',
                    borderRadius: 999,
                    color: '#fff',
                    fontSize: 18,
                  }}
                >
                  <RotateLeftOutlined onClick={onRotateLeft} />
                  <RotateRightOutlined onClick={onRotateRight} />
                  <ZoomOutOutlined disabled={scale === 1} onClick={onZoomOut} />
                  <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                </Space>
              ),
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Title level={5} style={{ margin: 0 }}>
            {detail.profile.firstName} {detail.profile.lastName}
          </Title>
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {status && (
              <StatusIndicator
                color={status.color}
                label={
                  (i18n.language === 'en' ? status.labelEn : status.labelEs) ||
                  t(`scenes.leads.scene.status_codes.${status.code}`, {
                    defaultValue: status.code,
                  })
                }
              />
            )}
            <Tag
              icon={<IdcardOutlined />}
              style={{
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {documentTypeLabel}: {documentValue}
            </Tag>
            <Tag
              icon={<EnvironmentOutlined />}
              style={{
                margin: 0,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {cityTypeLabel}: {cityValue}
            </Tag>
          </div>
          <LeadTagsEditor
            leadId={leadId}
            country={countryCode}
            tags={detail.tags ?? []}
            onSaved={handleTagsSaved}
            onDirtyChange={setTagsDirty}
          />
        </div>
        <Upload {...uploadProps} disabled={avatarUploading}>
          <Button
            type="text"
            size="small"
            icon={<CameraOutlined />}
            disabled={avatarUploading}
          >
            {t('scenes.leads.components.detail_lead_modal.avatar.upload')}
          </Button>
        </Upload>
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onCancel={() => confirmDiscardTags(handleModalCancel)}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={880}
      centered
      className="lead-detail-modal"
      title={null}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '20px 24px',
        },
      }}
    >
      {navigation && (
        <>
          <Button
            shape="circle"
            size="large"
            className="lead-nav-arrow lead-nav-arrow--left"
            icon={<LeftOutlined />}
            disabled={!navigation.canPrev || navigation.loading}
            onClick={() => confirmDiscardTags(navigation.onPrev)}
            aria-label={t(
              'scenes.leads.components.detail_lead_modal.header.prev_lead'
            )}
          />
          <Button
            shape="circle"
            size="large"
            className="lead-nav-arrow lead-nav-arrow--right"
            icon={<RightOutlined />}
            disabled={!navigation.canNext || navigation.loading}
            onClick={() => confirmDiscardTags(navigation.onNext)}
            aria-label={t(
              'scenes.leads.components.detail_lead_modal.header.next_lead'
            )}
          />
          {/* Lives on the backdrop, like the arrows, so it adds no row to the
              modal header. */}
          {(navigation.position || navigation.outOfFilter) && (
            <div className="lead-nav-position">
              {navigation.outOfFilter
                ? t(
                    'scenes.leads.components.detail_lead_modal.header.out_of_filter_label'
                  )
                : navigation.position &&
                  t(
                    'scenes.leads.components.detail_lead_modal.header.position',
                    {
                      current: navigation.position.current,
                      total: navigation.position.total,
                    }
                  )}
            </div>
          )}
        </>
      )}
      {loading || !detail ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : (
        <div
          key={leadId}
          className="lead-detail-content"
          style={
            {
              ['--lead-primary' as string]: primaryColor,
            } as React.CSSProperties
          }
        >
          {navigation?.outOfFilter && (
            <Alert
              type="warning"
              showIcon
              message={t(
                'scenes.leads.components.detail_lead_modal.messages.out_of_filter_alert'
              )}
              style={{ marginBottom: 16 }}
            />
          )}
          {renderHeader()}

          <Tabs
            defaultActiveKey="personal"
            items={[
              {
                key: 'personal',
                label: t(
                  'scenes.leads.components.detail_lead_modal.tabs.personal_data'
                ),
                children: (
                  <div>
                    <BasicInfoSection
                      detail={detail}
                      cities={cities}
                      citiesLoading={citiesLoading}
                      profileCatalogs={profileCatalogs}
                      onUpdated={handleSectionUpdated}
                    />
                    <IdentitySection
                      detail={detail}
                      onUpdated={handleSectionUpdated}
                    />
                  </div>
                ),
              },
              {
                key: 'studies',
                label: t(
                  'scenes.leads.components.detail_lead_modal.tabs.studies_availability'
                ),
                children: (
                  <OtherSection
                    detail={detail}
                    universities={universities}
                    universitiesLoading={universitiesLoading}
                    shifts={shifts}
                    shiftsLoading={shiftsLoading}
                    onUpdated={handleSectionUpdated}
                  />
                ),
              },
              {
                key: 'evaluation',
                label: t(
                  'scenes.leads.components.detail_lead_modal.tabs.evaluation'
                ),
                children: (
                  <EvaluationSection
                    detail={detail}
                    statuses={statuses}
                    onUpdated={handleSectionUpdated}
                  />
                ),
              },
              {
                key: 'payment',
                label: t(
                  'scenes.leads.components.detail_lead_modal.tabs.payment'
                ),
                children: (
                  <PaymentSections
                    detail={detail}
                    catalogs={paymentCatalogs}
                    cities={cities}
                    citiesLoading={citiesLoading}
                    onUpdated={handleSectionUpdated}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
}

export default DetailLeadModal;
